// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// useSpeaking watches a map of id -> MediaStream and returns the ids currently speaking
// (audio energy above a threshold, with a short hold to avoid flicker). Purely local: each
// client computes it from the streams it already has, no signalling. Muting disables the
// track so its energy is ~0 — a muted participant never lights up.

import { useEffect, useRef, useState } from "react";

const THRESHOLD = 0.04; // RMS (0..1) above which a participant counts as speaking
const HOLD_MS = 350; // keep highlighted briefly after dropping below (anti-flicker)
const DOMINANT_MS = 1800; // a new loudest speaker must hold the lead this long before the
// spotlight switches — so it doesn't flick around on brief audience noise.

let actx: AudioContext | null = null;

export interface SpeakingState {
  speaking: string[]; // ids currently speaking (for tile highlights)
  dominant: string | null; // the spotlight speaker — sticky + debounced
}

export function useSpeaking(streams: Record<string, MediaStream | null>): SpeakingState {
  const [speaking, setSpeaking] = useState<string[]>([]);
  const [dominant, setDominant] = useState<string | null>(null);
  const nodes = useRef(new Map<string, { source: MediaStreamAudioSourceNode; analyser: AnalyserNode; buf: Uint8Array<ArrayBuffer> }>());
  const lastAbove = useRef(new Map<string, number>());
  const dominantRef = useRef<string | null>(null);
  const leader = useRef<{ id: string; since: number } | null>(null); // a challenger building up time

  // Reconcile analysers with the current streams (add new, drop gone).
  useEffect(() => {
    const map = nodes.current;
    // Always drop analysers whose stream is gone (e.g. a call ended).
    for (const [id, n] of map) {
      if (!streams[id]?.getAudioTracks().length) {
        try {
          n.source.disconnect();
        } catch {
          /* already gone */
        }
        map.delete(id);
      }
    }
    // Only TOUCH the AudioContext when there's audio to analyse. The call views are mounted
    // (this hook runs) even when no call is active, and creating/resuming a context before
    // any user gesture spams the browser's autoplay warning. During a real call a gesture
    // (start/answer) has already happened, so resume() is silent.
    if (!Object.values(streams).some((s) => s?.getAudioTracks().length)) return;
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    actx ??= new Ctx();
    if (actx.state === "suspended") void actx.resume().catch(() => undefined);
    for (const [id, stream] of Object.entries(streams)) {
      if (!stream || map.has(id) || !stream.getAudioTracks().length) continue;
      try {
        const source = actx.createMediaStreamSource(stream);
        const analyser = actx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser); // tap only — NOT to destination, so no echo
        map.set(id, { source, analyser, buf: new Uint8Array(new ArrayBuffer(analyser.fftSize)) });
      } catch {
        /* a stream can only be sourced once; ignore */
      }
    }
  }, [streams]);

  // Poll energy ~10 Hz and update the speaking set.
  useEffect(() => {
    const tick = setInterval(() => {
      const now = Date.now();
      const next: string[] = [];
      let topId: string | null = null;
      let topRms = THRESHOLD;
      for (const [uid, n] of nodes.current) {
        n.analyser.getByteTimeDomainData(n.buf);
        let sum = 0;
        for (const v of n.buf) {
          const x = (v - 128) / 128;
          sum += x * x;
        }
        const rms = Math.sqrt(sum / n.buf.length);
        if (rms > THRESHOLD) lastAbove.current.set(uid, now);
        if (now - (lastAbove.current.get(uid) ?? 0) < HOLD_MS) next.push(uid);
        if (rms > topRms) {
          topRms = rms;
          topId = uid; // instantaneous loudest above threshold
        }
      }
      setSpeaking((prev) => (prev.length === next.length && prev.every((x) => next.includes(x)) ? prev : next));

      // Dominant speaker (spotlight): sticky + debounced. The current dominant stays unless
      // a DIFFERENT participant is the loudest continuously for DOMINANT_MS — so it doesn't
      // jump on a brief cough/laugh. Nobody speaking → keep the last dominant.
      if (topId && topId !== dominantRef.current) {
        if (leader.current?.id !== topId) leader.current = { id: topId, since: now };
        else if (now - leader.current.since >= DOMINANT_MS) {
          dominantRef.current = topId;
          leader.current = null;
          setDominant(topId);
        }
      } else {
        leader.current = null; // the dominant is still loudest (or silence) — no challenger
      }
    }, 100);
    return () => clearInterval(tick);
  }, []);

  // Tear analysers down on unmount.
  useEffect(() => {
    const map = nodes.current;
    return () => {
      for (const [, n] of map) {
        try {
          n.source.disconnect();
        } catch {
          /* ignore */
        }
      }
      map.clear();
    };
  }, []);

  return { speaking, dominant };
}
